<template>
    <div class="login-page">
        <div class="login-bg"></div>
        <div class="container">
            <div class="logo">
                <img src="@/assets/images/logo.svg" alt="" />
            </div>

            <form
                v-if="step === 1"
                class="login-form shadow-sm"
                @submit.prevent="requestCode"
            >
                <h1 class="form-title text-h1 text-bold text-center">Забули пароль?</h1>
                <p class="form-description text-h4 text-center">
                    Введіть email — надішлемо код для скидання пароля
                </p>
                <InputComponent
                    v-model="form.email"
                    type="email"
                    name="forgot_email"
                    autocomplete="email"
                    placeholder="E-mail"
                    :errors="v$.form.email.$errors"
                />
                <ButtonComponent
                    btn-style="gradient"
                    class="text-h2"
                    text="Надіслати код"
                    type="submit"
                    :disabled="submitting"
                />
                <router-link :to="{ name: 'loginPage' }" class="back-link text-h4 text-center">
                    Повернутися до входу
                </router-link>
            </form>

            <form
                v-else
                class="login-form shadow-sm"
                autocomplete="off"
                @submit.prevent="resetPassword"
            >
                <!-- decoy fields: prevent browser from autofilling login credentials -->
                <input
                    type="text"
                    name="username"
                    autocomplete="username"
                    tabindex="-1"
                    aria-hidden="true"
                    class="autofill-decoy"
                />
                <input
                    type="password"
                    name="password"
                    autocomplete="current-password"
                    tabindex="-1"
                    aria-hidden="true"
                    class="autofill-decoy"
                />

                <h1 class="form-title text-h1 text-bold text-center">Новий пароль</h1>
                <p class="form-description text-h4 text-center">
                    Введіть код з листа та новий пароль
                </p>
                <InputComponent
                    v-model="form.email"
                    type="email"
                    name="reset_email"
                    autocomplete="email"
                    placeholder="E-mail"
                    disabled
                />
                <InputComponent
                    v-model="form.code"
                    type="text"
                    name="otp_code"
                    autocomplete="one-time-code"
                    inputmode="numeric"
                    placeholder="Код з email"
                    :errors="v$.form.code.$errors"
                />
                <InputComponent
                    v-model="form.password"
                    type="password"
                    name="new_password"
                    autocomplete="new-password"
                    placeholder="Новий пароль"
                    :errors="v$.form.password.$errors"
                />
                <InputComponent
                    v-model="form.password_confirmation"
                    type="password"
                    name="new_password_confirmation"
                    autocomplete="new-password"
                    placeholder="Підтвердження пароля"
                    :errors="v$.form.password_confirmation.$errors"
                />
                <ButtonComponent
                    btn-style="gradient"
                    class="text-h2 submit-btn"
                    text="Зберегти пароль"
                    type="submit"
                    :disabled="submitting"
                />
                <button
                    type="button"
                    class="back-link text-h4 text-center"
                    :disabled="submitting"
                    @click="backToEmailStep"
                >
                    Надіслати код ще раз
                </button>
            </form>
        </div>
    </div>
</template>

<script>
import InputComponent from '@/components/form/InputComponent.vue'
import ButtonComponent from '@/components/ButtonComponent.vue'
import { useVuelidate } from '@vuelidate/core'
import { email, helpers, maxLength, minLength, required, sameAs } from '@vuelidate/validators'
import fetchWrapper from '@/helpers/fetch-wrapper.js'

export default {
    name: 'ForgotPasswordView',
    components: { ButtonComponent, InputComponent },
    setup() {
        const v$ = useVuelidate({ $scope: false })

        return { v$ }
    },
    data() {
        return {
            step: 1,
            submitting: false,
            form: {
                email: '',
                code: '',
                password: '',
                password_confirmation: ''
            }
        }
    },
    validations() {
        return {
            form: {
                email: {
                    required: helpers.withMessage("Email є обов'язковим", required),
                    min: helpers.withMessage('Електронна пошта занадто коротка', minLength(8)),
                    max: helpers.withMessage('Електронна пошта занадто довга', maxLength(255)),
                    email: helpers.withMessage('Недійсна адреса електронної пошти', email)
                },
                code: {
                    required: helpers.withMessage("Код є обов'язковим", required),
                    min: helpers.withMessage('Код має містити 6 символів', minLength(6)),
                    max: helpers.withMessage('Код має містити 6 символів', maxLength(6))
                },
                password: {
                    required: helpers.withMessage("Новий пароль є обов'язковим", required),
                    min: helpers.withMessage('Пароль має містити щонайменше 8 символів', minLength(8)),
                    max: helpers.withMessage('Пароль занадто довгий', maxLength(255))
                },
                password_confirmation: {
                    required: helpers.withMessage("Підтвердження пароля є обов'язковим", required),
                    sameAs: helpers.withMessage('Паролі не співпадають', sameAs(this.form.password))
                }
            }
        }
    },
    methods: {
        backToEmailStep() {
            this.step = 1
            this.form.code = ''
            this.form.password = ''
            this.form.password_confirmation = ''
            this.v$.form.code.$reset()
            this.v$.form.password.$reset()
            this.v$.form.password_confirmation.$reset()
        },

        async requestCode() {
            this.v$.form.email.$touch()
            if (this.v$.form.email.$invalid) {
                return
            }

            this.submitting = true

            try {
                const response = await fetchWrapper.post('/auth/password/email', {
                    email: this.form.email
                })
                this.$toast.success(
                    response.message || 'Якщо обліковий запис існує, код надіслано на email'
                )
                this.step = 2
            } finally {
                this.submitting = false
            }
        },

        async resetPassword() {
            this.v$.form.code.$touch()
            this.v$.form.password.$touch()
            this.v$.form.password_confirmation.$touch()

            if (
                this.v$.form.code.$invalid ||
                this.v$.form.password.$invalid ||
                this.v$.form.password_confirmation.$invalid
            ) {
                return
            }

            this.submitting = true

            try {
                const response = await fetchWrapper.post('/auth/password/reset', {
                    email: this.form.email,
                    code: this.form.code,
                    password: this.form.password,
                    password_confirmation: this.form.password_confirmation
                })
                this.$toast.success(response.message || 'Пароль скинуто успішно')
                this.$router.push({ name: 'loginPage' })
            } finally {
                this.submitting = false
            }
        }
    }
}
</script>

<style scoped lang="scss">
.login-page {
    position: relative;
    height: 100%;
    padding: 5rem 0 2rem;

    .login-bg {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 50%;
        background-image: url('@/assets/images/login-bg.svg');
        background-repeat: no-repeat;
        background-position: top left;
        background-size: cover;
        border-radius: 0 0 2rem 2rem;
        z-index: -1;
    }

    .logo {
        display: flex;
        padding: 1rem 1.5rem;
        background: $white;
        border-radius: 1.25rem;
        width: fit-content;
        margin: 0 auto;
    }

    .container {
        height: 100%;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
    }
}

.login-form {
    position: absolute;
    top: 50%;
    left: 50%;
    width: calc(100% - 2rem);
    max-width: 30rem;
    background: $white;
    padding: 3rem 1rem;
    border-radius: 2rem;
    transform: translate(-50%, -50%);

    .form-title {
        margin-bottom: 0.8rem;
    }

    .form-description {
        margin-bottom: 2rem;
    }

    .submit-btn {
        margin-top: 0.5rem;
    }
}

.autofill-decoy {
    position: absolute;
    left: -9999px;
    width: 1px;
    height: 1px;
    opacity: 0;
    pointer-events: none;
}

.back-link {
    display: block;
    width: 100%;
    margin-top: 1.25rem;
    color: $grey;
    text-decoration: none;
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
}

@media screen and (max-height: 750px) {
    .login-page {
        padding: 2rem 0;
    }
}
</style>
